import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { zustandStorage } from '@/lib/storage';
import { setSoundEnabled } from '@/lib/sound';
import { DEFAULT_WALLPAPER, wallpapers, type WallpaperPosition } from '@/config/wallpapers';
import { DEFAULT_THEME, type ThemeId } from '@/theme/themes';
import {
  DEFAULT_SCREEN_SAVER,
  DEFAULT_WAIT_MINUTES,
  type ScreenSaverId,
} from '@/config/screensavers';

/**
 * Recycle Bin ▸ Properties, which in XP was a real property sheet with real
 * consequences. Two of these three still have them here.
 */
export interface RecycleBinSettings {
  /** "Do not move files to the Recycle Bin. Remove files immediately when deleted." */
  bypass: boolean;
  /** "Display delete confirmation dialog." */
  confirm: boolean;
  /**
   * "Maximum size of Recycle Bin (percent of each drive)." Remembered and
   * shown, and that is all it can do: the quota it governed was measured in
   * megabytes, and nothing on this disk has a size.
   */
  maxPercent: number;
}

interface SeenFlags {
  /** The first-visit tray balloon. */
  balloon: boolean;
}

interface SettingsState {
  wallpaper: string;
  wallpaperPosition: WallpaperPosition;
  themeId: ThemeId;
  soundEnabled: boolean;
  screenSaverId: ScreenSaverId;
  /** Idle minutes before the screen saver runs. */
  screenSaverWait: number;
  /**
   * Bumped by Display Properties' Preview button. A counter rather than a
   * boolean because "run it now" is an event, and an event that has to be
   * unset again is a bug waiting to happen.
   */
  screenSaverPreview: number;
  recycleBin: RecycleBinSettings;
  seen: SeenFlags;

  setWallpaper(id: string): void;
  setWallpaperPosition(pos: WallpaperPosition): void;
  setTheme(id: ThemeId): void;
  setSoundEnabled(value: boolean): void;
  setScreenSaver(id: ScreenSaverId): void;
  setScreenSaverWait(minutes: number): void;
  previewScreenSaver(): void;
  setRecycleBin(next: Partial<RecycleBinSettings>): void;
  markSeen(flag: keyof SeenFlags): void;
  reset(): void;
}

const initial = {
  wallpaper: DEFAULT_WALLPAPER,
  wallpaperPosition: 'stretch' as WallpaperPosition,
  themeId: DEFAULT_THEME,
  soundEnabled: true,
  screenSaverId: DEFAULT_SCREEN_SAVER,
  screenSaverWait: DEFAULT_WAIT_MINUTES,
  screenSaverPreview: 0,
  // XP's own defaults: the bin catches deletes, asks first, and is allowed a
  // tenth of the drive.
  recycleBin: { bypass: false, confirm: true, maxPercent: 10 },
  seen: { balloon: false },
};

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      ...initial,

      setWallpaper: (id) => set({ wallpaper: id }),
      setWallpaperPosition: (pos) => set({ wallpaperPosition: pos }),
      setTheme: (id) => set({ themeId: id }),
      setSoundEnabled: (value) => {
        setSoundEnabled(value);
        set({ soundEnabled: value });
      },
      setScreenSaver: (id) => set({ screenSaverId: id }),
      setScreenSaverWait: (minutes) => set({ screenSaverWait: Math.max(1, minutes) }),
      previewScreenSaver: () => set((s) => ({ screenSaverPreview: s.screenSaverPreview + 1 })),
      setRecycleBin: (next) => set((s) => ({ recycleBin: { ...s.recycleBin, ...next } })),
      markSeen: (flag) => set((s) => ({ seen: { ...s.seen, [flag]: true } })),
      reset: () => set({ ...initial }),
    }),
    {
      name: 'teonio.settings',
      version: 4,
      storage: createJSONStorage(() => zustandStorage),
      partialize: (s) => ({
        wallpaper: s.wallpaper,
        wallpaperPosition: s.wallpaperPosition,
        themeId: s.themeId,
        soundEnabled: s.soundEnabled,
        screenSaverId: s.screenSaverId,
        screenSaverWait: s.screenSaverWait,
        recycleBin: s.recycleBin,
        seen: s.seen,
      }),
      migrate: (persisted, version) => {
        // v0 had no `seen` object, v1 no screen saver and v3 nothing for the
        // Recycle Bin. Every one is answered the same way: start from the
        // defaults and lay the stored keys on top, so an old blob keeps its
        // wallpaper rather than being dropped for the sake of a new key.
        const state =
          version < 4 && persisted && typeof persisted === 'object'
            ? ({ ...initial, ...(persisted as object) } as SettingsState)
            : (persisted as SettingsState);

        // v3 replaced the drawn stand-in wallpapers with the real ones, which
        // retired `azul` and `crystal`. The desktop would already fall back to
        // Bliss via `findWallpaper`, but the *stored* id has to go too, or
        // Display Properties opens with nothing selected in the list.
        if (state && !wallpapers.some((w) => w.id === state.wallpaper)) {
          return { ...state, wallpaper: DEFAULT_WALLPAPER };
        }
        return state;
      },
      onRehydrateStorage: () => (state) => {
        if (state) setSoundEnabled(state.soundEnabled);
      },
    },
  ),
);
